import * as SliderPrimitive from "@radix-ui/react-slider";
import * as React from "react";
import { cx } from "../internal/cx.js";

type SliderPrimitiveProps = React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>;

export interface SliderProps extends SliderPrimitiveProps {
  /** Optional visible label rendered above the slider. */
  label?: React.ReactNode;
  /** Optional visible value text rendered opposite the label. */
  valueLabel?: React.ReactNode;
  thumbCount?: number;
  /**
   * Per-thumb accessible labels. Each thumb in Radix Slider carries
   * `role="slider"` and needs its own name. If omitted we fall back to
   * the Root's `aria-label` (suffixed `(min)` / `(max)` for ranges) so
   * axe's `aria-input-field-name` rule passes.
   */
  thumbLabels?: readonly string[];
}

/**
 * Continuous-range slider. Supports single-thumb and range (`thumbCount=2`)
 * modes. The thumb count is inferred from the `value` / `defaultValue`
 * array length when neither `thumbCount` nor a `value` is supplied,
 * falling back to a single thumb.
 */
export const Slider = React.forwardRef<React.ElementRef<typeof SliderPrimitive.Root>, SliderProps>(
  function Slider(
    { className, defaultValue, label, thumbCount, thumbLabels, value, valueLabel, ...props },
    ref,
  ) {
    const resolvedThumbCount = thumbCount ?? value?.length ?? defaultValue?.length ?? 1;
    const rootLabel = typeof props["aria-label"] === "string" ? props["aria-label"] : undefined;
    const labelFor = (index: number): string => {
      if (thumbLabels?.[index]) return thumbLabels[index] ?? "";
      if (rootLabel && resolvedThumbCount === 1) return rootLabel;
      if (rootLabel) return `${rootLabel} (${index === 0 ? "min" : "max"})`;
      return `Slider thumb ${index + 1}`;
    };

    const sliderRoot = (
      <SliderPrimitive.Root
        ref={ref}
        className={cx("cw-v2-slider", className)}
        defaultValue={defaultValue}
        value={value}
        {...props}
      >
        <SliderPrimitive.Track className="cw-v2-slider__track">
          <SliderPrimitive.Range className="cw-v2-slider__range" />
        </SliderPrimitive.Track>
        {Array.from({ length: resolvedThumbCount }).map((_, index) => (
          <SliderPrimitive.Thumb
            // biome-ignore lint/suspicious/noArrayIndexKey: stable count, no reordering
            key={index}
            aria-label={labelFor(index)}
            className="cw-v2-slider__thumb"
          />
        ))}
      </SliderPrimitive.Root>
    );

    if (!label && !valueLabel) return sliderRoot;

    return (
      <div className="cw-v2-slider-field">
        <div className="cw-v2-slider-field__header">
          {label ? <span className="cw-v2-slider-field__label">{label}</span> : <span />}
          {valueLabel ? <span className="cw-v2-slider-field__value">{valueLabel}</span> : null}
        </div>
        {sliderRoot}
      </div>
    );
  },
);
Slider.displayName = "Slider";
